/**
 * Stage3_Image - 插图生成阶段
 * 
 * 用户旅程第四步：生成12页绘本插图
 * 路由: /create/stage/3?storyId=xxx
 * 
 * 功能：
 * - 显示左侧角色信息（固定）
 * - 右侧12宫格缩略图展示
 * - 批量生成全部插图
 * - 单页重生成
 * - 大图预览Modal
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

// 获取认证请求头
const getAuthHeaders = (contentType = false): HeadersInit => {
  const token = localStorage.getItem('token');
  const headers: Record<string, string> = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  if (contentType) {
    headers['Content-Type'] = 'application/json';
  }
  return headers;
};
import { motion, AnimatePresence } from 'framer-motion';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import { StepIndicator } from '../../components/create/StepIndicator';
import {
  Loader2,
  RefreshCw,
  ChevronRight,
  ChevronLeft,
  CheckCircle,
  User,
  Image as ImageIcon,
  Eye,
  AlertCircle,
  X,
  AlertTriangle,
} from 'lucide-react';

interface StoryData {
  id: string;
  title: string;
  stage: string;
  pageCount: number;
  artStyle: string;
  textContent?: {
    pages: Array<{
      pageNumber: number;
      text: string;
      illustrationPrompt?: string;
    }>;
  };
  characterData?: {
    name: string;
    imageUrl: string;
    description: string;
    seed?: string;
    clothing?: string;
    keyTraits?: string[];
    colorTheme?: string;
    identityCardUrl?: string;
  };
  photoUrl?: string;
  imageUrls?: string[];
  audioUrls?: string[];
}

interface GenerationProgress {
  status: 'idle' | 'generating' | 'completed' | 'failed';
  currentPage: number;
  totalPages: number;
  completedPages: number;
  failedPages: number[];
  imageUrls: string[];
  error?: string;
}

export default function Stage3_Image() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const storyId = searchParams.get('storyId');
  
  const [story, setStory] = useState<StoryData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isGeneratingAll, setIsGeneratingAll] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [regeneratingPage, setRegeneratingPage] = useState<number | null>(null);
  const [progress, setProgress] = useState<GenerationProgress | null>(null);
  const [generationError, setGenerationError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isCompleting, setIsCompleting] = useState(false);
  
  // 大图预览Modal
  const [previewPage, setPreviewPage] = useState<number | null>(null);
  
  // 重绘参考图选择Modal
  const [regenerateModalPage, setRegenerateModalPage] = useState<number | null>(null);
  const [selectedRefUrls, setSelectedRefUrls] = useState<string[]>([]);
  
  // 全部重绘确认对话框
  const [showRegenerateAllConfirm, setShowRegenerateAllConfirm] = useState(false);
  const [regenerateAllCostInfo, setRegenerateAllCostInfo] = useState<{
    pageCount: number;
    freePages: number;
    pricePerPage: number;
    chargeablePages: number;
    cost: number;
  } | null>(null);
  
  // 使用 ref 保存 interval，避免闭包问题
  const progressIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isPollingRef = useRef(false);
  const isSubmittingRef = useRef(false);
  const isRegeneratingRef = useRef(false);

  // 构建图片URL（处理相对路径）
  const buildImageUrl = (imageUrl: string | undefined): string => {
    if (!imageUrl) return '';
    if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
      return imageUrl;
    }
    return imageUrl.startsWith('/') ? imageUrl : `/${imageUrl}`;
  };

  useEffect(() => {
    if (!storyId) {
      navigate('/create');
      return;
    }
    
    // 加载故事数据，并检查是否需要自动开始轮询
    const loadStory = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/stories/${storyId}`, { 
          cache: 'no-store',
          headers: getAuthHeaders(),
        });
        const result = await response.json();
        const data = result.data;
        // 强制浏览器重新下载图片（避免 COS 缓存）
        if (data?.imageUrls) {
          data.imageUrls = addCacheBust(data.imageUrls);
        }
        setStory(data);
        
        // 获取进度
        const progressRes = await fetch(`${API_BASE_URL}/stories/${storyId}/stage/3/progress`, { 
          cache: 'no-store',
          headers: getAuthHeaders(),
        });
        const progressData = await progressRes.json();
        
        if (progressData.success) {
          setProgress(progressData.data.progress);
          
          // 如果后端正在生成中，自动开始轮询
          if (progressData.data.progress?.status === 'generating') {
            console.log('[Stage3] Detected ongoing generation, starting polling...');
            setIsGeneratingAll(true);
          }
        }
      } catch (error) {
        console.error('获取故事失败:', error);
        alert('获取故事失败');
      } finally {
        setIsLoading(false);
      }
    };
    
    loadStory();
    
    return () => {
      // 组件卸载时清理轮询
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
    };
  }, [storyId]);

  // 使用 useCallback 避免函数重新创建
  const fetchProgress = useCallback(async () => {
    if (!storyId || !isPollingRef.current) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/stories/${storyId}/stage/3/progress`, { 
        cache: 'no-store',
        headers: getAuthHeaders(),
      });
      const result = await response.json();
      
      if (result.success) {
        setProgress(result.data.progress);
        
        // 关键：同时更新 story.imageUrls，让图片立即显示
        // 优先使用 progress 中的实时 imageUrls（生成过程中最新），回退到接口返回的 imageUrls
        let latestImageUrls = result.data.progress?.imageUrls?.length > 0
          ? result.data.progress.imageUrls
          : result.data.imageUrls;
        // 强制浏览器重新下载新图片（避免 COS/浏览器缓存）
        if (latestImageUrls?.length > 0) {
          latestImageUrls = addCacheBust(latestImageUrls) || [];
          setStory(prev => {
            if (!prev) return prev;
            return {
              ...prev,
              imageUrls: latestImageUrls,
            };
          });
        }
        
        // 如果已完成或失败，停止轮询并刷新完整故事数据
        if (result.data.progress?.status === 'completed' || result.data.progress?.status === 'failed') {
          isPollingRef.current = false;
          if (progressIntervalRef.current) {
            clearInterval(progressIntervalRef.current);
            progressIntervalRef.current = null;
          }
          setIsGeneratingAll(false);
          // 生成结束后拉取最新故事数据（确保 imageUrls 与数据库同步）
          fetchStory();
        }
      }
    } catch (error) {
      console.error('获取进度失败:', error);
    }
  }, [storyId]);

  // 当开始生成时，启动轮询
  useEffect(() => {
    if (isGeneratingAll && storyId) {
      // 避免重复启动轮询
      if (progressIntervalRef.current) {
        return;
      }
      
      isPollingRef.current = true;
      console.log('[Stage3] Starting polling...');
      
      // 立即执行一次
      fetchProgress();
      
      // 每3秒轮询一次
      const interval = setInterval(() => {
        fetchProgress();
      }, 3000);
      
      progressIntervalRef.current = interval;
    } else {
      // 停止轮询
      if (progressIntervalRef.current) {
        console.log('[Stage3] Stopping polling...');
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
      isPollingRef.current = false;
    }
    
    // 清理函数
    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
    };
  }, [isGeneratingAll, storyId, fetchProgress]);

  // 给图片 URL 添加缓存清除参数，强制浏览器重新下载（覆盖 COS 同名文件后必用）
  const addCacheBust = (urls: string[] | undefined): string[] | undefined => {
    if (!urls) return urls;
    return urls.map(url => {
      if (!url) return url;
      const sep = url.includes('?') ? '&' : '?';
      return `${url}${sep}_cb=${Date.now()}`;
    });
  };

  const addCacheBustSingle = (url: string | undefined): string | undefined => {
    if (!url) return url;
    const sep = url.includes('?') ? '&' : '?';
    return `${url}${sep}_cb=${Date.now()}`;
  };

  // 刷新故事数据（用于重生成后刷新）
  const fetchStory = async () => {
    if (!storyId) return;
    
    try {
      // URL 加时间戳，防止浏览器/中间件缓存 API 响应
      const response = await fetch(`${API_BASE_URL}/stories/${storyId}?_t=${Date.now()}`, { 
        cache: 'no-store',
        headers: getAuthHeaders(),
      });
      const result = await response.json();
      const data = result.data;
      // 强制浏览器重新下载图片（COS 文件已覆盖但 URL 未变）
      if (data?.imageUrls) {
        data.imageUrls = addCacheBust(data.imageUrls);
      }
      setStory(data);
    } catch (error) {
      console.error('获取故事失败:', error);
    }
  };

  // 生成全部插图
  const generateAll = async () => {
    if (isSubmittingRef.current) {
      console.log('[Stage3] Double click blocked by ref lock');
      return;
    }
    isSubmittingRef.current = true;
    setGenerationError(null);
    setIsSubmitting(true);
    setIsGeneratingAll(true);
    // 立即清空本地图片，让页面显示占位符而不是旧图
    setStory(prev => prev ? { ...prev, imageUrls: [] } : prev);
    // 设置初始进度，提供即时视觉反馈
    const totalPages = story?.textContent?.pages?.length || 12;
    setProgress({
      status: 'generating',
      currentPage: 1,
      totalPages,
      completedPages: 0,
      failedPages: [],
      imageUrls: new Array(totalPages).fill(''),
    });
    try {
      console.log('[Stage3] Starting image generation...');
      const response = await fetch(`${API_BASE_URL}/stories/${storyId}/stage/3/images`, {
        method: 'POST',
        headers: getAuthHeaders(true),
      });
      
      if (!response.ok) {
        const errText = await response.text();
        console.error('[Stage3] Generation start failed:', response.status, errText);
        throw new Error(`HTTP ${response.status}: ${errText}`);
      }
      
      const result = await response.json();
      
      if (result.success) {
        console.log('[Stage3] Image generation started, task:', result.data);
        // 轮询会自动开始
      } else {
        console.error('[Stage3] Generation start failed:', result.error);
        setGenerationError(result.error || '生成启动失败');
        setIsGeneratingAll(false);
      }
    } catch (error: any) {
      console.error('[Stage3] Generation failed:', error);
      const errMsg = error?.message || '';
      const isAlreadyGenerating = errMsg.includes('429') && errMsg.includes('isGenerating');
      if (isAlreadyGenerating) {
        setGenerationError('插图正在后台生成中，请勿重复点击');
        // isGeneratingAll 保持 true，轮询继续
      } else {
        setGenerationError(error?.message || '生成启动失败，请检查网络或刷新页面重试');
        setIsGeneratingAll(false);
      }
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  // 打开重绘参考图选择Modal
  const openRegenerateModal = (pageNumber: number) => {
    setRegenerateModalPage(pageNumber);
    // 默认不选任何参考图，由用户自行决定（避免默认全选导致拼图）
    setSelectedRefUrls([]);
  };
  
  // 打开全部重绘确认对话框
  const openRegenerateAllConfirm = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/stories/${storyId}/stage/3/regenerate-all-cost`, {
        method: 'POST',
        headers: getAuthHeaders(true),
      });
      const result = await res.json();
      if (!result.success) {
        setGenerationError(result.error || '获取费用信息失败');
        return;
      }
      setRegenerateAllCostInfo(result.data);
      setShowRegenerateAllConfirm(true);
    } catch (error: any) {
      setGenerationError(error?.message || '获取费用信息失败');
    }
  };
  
  // 关闭全部重绘确认对话框
  const closeRegenerateAllConfirm = () => {
    setShowRegenerateAllConfirm(false);
    setRegenerateAllCostInfo(null);
  };

  // 关闭重绘Modal
  const closeRegenerateModal = () => {
    setRegenerateModalPage(null);
    setSelectedRefUrls([]);
  };

  // 重生成单页（支持自定义参考图）
  const regenerateImage = async (pageNumber: number, customRefUrls?: string[]) => {
    if (isRegeneratingRef.current) {
      console.log('[Stage3] Regenerate double click blocked by ref lock');
      return;
    }
    isRegeneratingRef.current = true;

    // 先请求预检接口获取费用信息
    const costRes = await fetch(`${API_BASE_URL}/stories/${storyId}/stage/3/regenerate-cost`, {
      method: 'POST',
      headers: getAuthHeaders(true),
      body: JSON.stringify({ pageNumber }),
    });
    const costData = await costRes.json();
    if (!costData.success) {
      setGenerationError(costData.error || '获取费用信息失败');
      return;
    }
    const confirmMsg = costData.data.cost > 0
      ? `本次重绘将扣除 ¥${costData.data.cost.toFixed(2)}，确认继续？`
      : `本次为第 ${costData.data.usedCount + 1} 次免费重绘（共 ${costData.data.freeCount} 次），确认重新生成？`;
    if (!window.confirm(confirmMsg)) return;

    setRegeneratingPage(pageNumber);
    setGenerationError(null);
    try {
      const body: any = { pageNumber };
      if (customRefUrls && customRefUrls.length > 0) {
        body.referenceImageUrls = customRefUrls;
      }
      
      const response = await fetch(`${API_BASE_URL}/stories/${storyId}/stage/3/regenerate`, {
        method: 'POST',
        headers: getAuthHeaders(true),
        body: JSON.stringify(body),
      });
      const result = await response.json();

      if (result.success) {
        console.log(`第${pageNumber}页插图已重新生成:`, result.data?.imageUrl);
        // 立即用返回的 URL 更新本地状态，不依赖 fetchStory
        const returnedUrl = result.data?.imageUrl;
        let localUpdatedUrl: string | undefined;
        if (returnedUrl) {
          localUpdatedUrl = addCacheBustSingle(returnedUrl) || returnedUrl;
          setStory(prev => {
            if (!prev) return prev;
            const newUrls = [...(prev.imageUrls || [])];
            newUrls[pageNumber - 1] = localUpdatedUrl!;
            return { ...prev, imageUrls: newUrls };
          });
        }
        // 等待数据库数据刷新完成，确保和本地一致
        await fetchStory();
        // 防御性校验：如果 fetchStory 返回的 URL 仍是旧文件（文件名不同但时间戳更旧），保留本地新 URL
        if (localUpdatedUrl) {
          setStory(prev => {
            if (!prev) return prev;
            const serverUrl = prev.imageUrls?.[pageNumber - 1];
            if (!serverUrl) return prev;
            // 提取文件名中的时间戳进行比较
            const extractTimestamp = (url: string) => {
              const match = url.match(/page-\d+-(\d+)\.webp/);
              return match ? parseInt(match[1]) : 0;
            };
            const localTs = extractTimestamp(localUpdatedUrl);
            const serverTs = extractTimestamp(serverUrl);
            if (localTs > serverTs) {
              console.log(`[Stage3] Server URL older than local, keeping local URL for page ${pageNumber}`);
              const newUrls = [...(prev.imageUrls || [])];
              newUrls[pageNumber - 1] = localUpdatedUrl;
              return { ...prev, imageUrls: newUrls };
            }
            return prev;
          });
        }
        // 显示成功提示
        setSuccessMessage(`第 ${pageNumber} 页插图重绘成功`);
        setTimeout(() => setSuccessMessage(null), 3000);
      } else if (result.needRecharge) {
        setGenerationError('余额不足，请充值后再试');
      } else {
        setGenerationError('重生成失败: ' + result.error);
      }
    } catch (error: any) {
      console.error('重生成失败:', error);
      setGenerationError(error?.message || '重生成失败，请检查网络或刷新页面重试');
    } finally {
      isRegeneratingRef.current = false;
      setRegeneratingPage(null);
      closeRegenerateModal();
    }
  };

  // 返回上一阶段（修改配音）
  const goToPrevStage = () => {
    const hasImagesBefore = (story?.imageUrls || []).filter(Boolean).length > 0;
    if (hasImagesBefore) {
      const confirmed = window.confirm(
        '⚠️ 返回修改音频后，当前已生成的插图内容不会受影响。\n\n' +
        '但如果修改了文案，插图与文案可能不匹配，需要重新生成插图。\n\n' +
        '确定要返回修改配音吗？'
      );
      if (!confirmed) return;
    }
    navigate(`/create/stage/2?storyId=${storyId}&flow=ai`);
  };

  // 进入下一阶段 - 跳转到阅读器
  const goToNextStage = async () => {
    setIsCompleting(true);
    try {
      const response = await fetch(`${API_BASE_URL}/stories/${storyId}/stage/3/next`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || `请求失败: ${response.status}`);
      }

      // 短暂延迟让用户看到进度条"完成"状态变化
      await new Promise(resolve => setTimeout(resolve, 600));
      // 跳转到阅读器
      navigate(`/reader/${storyId}`);
    } catch (error: any) {
      console.error('进入下一阶段失败:', error);
      alert(error?.message || '进入下一阶段失败');
      setIsCompleting(false);
    }
  };

  // 获取图片状态 - 优先使用 progress 中的实时数据
  const getImageStatus = (index: number): { status: 'pending' | 'generating' | 'done' | 'failed'; imageUrl?: string } => {
    const pageNumber = index + 1;
    
    // 优先从 progress 获取实时数据（轮询时更新）
    const progressImageUrl = progress?.imageUrls?.[index];
    const storyImageUrl = story?.imageUrls?.[index];
    const imageUrl = progressImageUrl || storyImageUrl;
    
    // 如果已有图片URL，显示完成
    if (imageUrl) {
      return { status: 'done', imageUrl };
    }
    
    // 根据进度判断状态
    if (progress) {
      // 如果这一页在失败列表中
      if (progress.failedPages?.includes(pageNumber)) {
        return { status: 'failed' };
      }
      
      // 如果正在生成这一页
      if (progress.status === 'generating') {
        if (progress.currentPage === pageNumber) {
          return { status: 'generating' };
        }
        // 如果这一页已经处理过（currentPage 已经前进）但没有图片URL，说明失败了
        if (pageNumber < progress.currentPage) {
          return { status: 'failed' };
        }
      }
    }
    
    return { status: 'pending' };
  };

  // 计算完成数量
  const completedCount = story?.imageUrls?.filter(Boolean).length || 0;
  const totalCount = story?.textContent?.pages?.length || 12;
  const hasGenerated = completedCount > 0;
  const allCompleted = completedCount === totalCount;

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50">
        <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
      </div>
    );
  }

  const currentStep = isCompleting ? 4 : 3;
  const pages = story?.textContent?.pages || [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50 py-8">
      <div className="w-full flex justify-center">
        <div className="w-full max-w-7xl px-4 sm:px-6 lg:px-8 flex flex-col gap-6">
        {/* 上游内容提示 */}
        {(story?.imageUrls || []).filter(Boolean).length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-start gap-3"
          >
            <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-amber-800">
              <p className="font-medium">当前插图已生成</p>
              <p className="text-amber-700 mt-0.5">如需修改文案或音频，请点击"上一步"返回对应阶段。修改后请回到本阶段重新生成插图以保持内容一致。</p>
            </div>
          </motion.div>
        )}

        {/* 步骤导航 */}
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="mb-6 w-full">
          <StepIndicator currentStep={currentStep} />
        </motion.div>

        {/* 页面标题 */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 text-center mb-2">
            {hasGenerated ? '预览绘本插图' : '生成绘本插图'}
          </h1>
          <p className="text-gray-600">
            {hasGenerated 
              ? `已生成 ${completedCount}/${totalCount} 页插图，点击缩略图可查看大图`
              : `点击下方按钮为${totalCount}页故事生成精美插图`
            }
          </p>
          {isGeneratingAll && progress && (
            <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-purple-100 text-purple-700 rounded-full">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>生成中... {progress.completedPages}/{progress.totalPages} 页</span>
            </div>
          )}
          {/* 生成错误提示 - 只要有错误就显示 */}
          {generationError && (
            <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-red-100 text-red-700 rounded-full max-w-full">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span className="truncate">
                {generationError}
              </span>
            </div>
          )}
          {/* 成功提示 */}
          {successMessage && (
            <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-green-100 text-green-700 rounded-full">
              <CheckCircle className="w-4 h-4 flex-shrink-0" />
              <span>{successMessage}</span>
            </div>
          )}
          {/* 后端返回失败状态提示 */}
          {!isGeneratingAll && progress?.status === 'failed' && !generationError && (
            <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-red-100 text-red-700 rounded-full max-w-full">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span className="truncate">
                {progress?.error || '部分或全部页面生成失败，请检查网络或点击失败页面重试'}
              </span>
            </div>
          )}
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* 左侧：角色信息 */}
          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} className="lg:col-span-1">
            <Card className="sticky top-8 bg-white rounded-2xl shadow-sm border border-slate-200 p-4 sm:p-6">
              <div className="flex flex-col gap-6">
                <div className="flex items-center gap-2">
                  <User className="w-5 h-5 text-purple-500" />
                  <h2 className="text-lg font-semibold">绘本角色</h2>
                </div>
                
                <div className="text-center">
                  {/* 主图：优先显示卡通肖像（identityCardUrl），否则显示真实照片 */}
                  <div className="flex justify-center mb-4">
                    <div className="w-32 h-32 rounded-xl overflow-hidden bg-slate-100 relative">
                      {story?.characterData?.identityCardUrl || story?.characterData?.imageUrl ? (
                        <img
                          src={buildImageUrl(story.characterData.identityCardUrl || story.characterData.imageUrl)}
                          alt={story.characterData.name}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none';
                            const parent = (e.target as HTMLImageElement).parentElement;
                            if (parent) {
                              const fallback = document.createElement('div');
                              fallback.className = 'w-full h-full flex items-center justify-center';
                              fallback.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-gray-300"><rect width="18" height="18" x="3" y="3" rx="2" ry="2"/><circle cx="9" cy="9" r="2"/><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/></svg>';
                              parent.appendChild(fallback);
                            }
                          }}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <ImageIcon className="w-12 h-12 text-gray-300" />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* 参考原型小图：当有卡通肖像时，显示真实照片作为对照 */}
                  {story?.characterData?.identityCardUrl && story?.photoUrl && (
                    <div className="flex items-center justify-center gap-2 mb-3">
                      <span className="text-xs text-slate-400">参考原型</span>
                      <div className="w-8 h-8 rounded-lg overflow-hidden border border-slate-200 bg-slate-100 flex items-center justify-center">
                        <img
                          src={buildImageUrl(story.photoUrl)}
                          alt="参考原型"
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                        />
                      </div>
                    </div>
                  )}

                  <h3 className="font-medium text-gray-900">{story?.characterData?.name || 'Benny'}</h3>
                  <p className="text-sm text-slate-500 line-clamp-3 mt-2">
                    {story?.characterData?.description || 'A brave little rabbit'}
                  </p>
                  
                  {/* AI提取的角色特征 */}
                  {story?.characterData?.clothing && (
                    <div className="mt-3 text-xs text-indigo-600 bg-indigo-50 rounded-lg px-3 py-1.5">
                      <span className="font-medium">服装：</span>{story.characterData.clothing}
                    </div>
                  )}
                  
                  {story?.characterData?.keyTraits && (story.characterData.keyTraits as string[]).length > 0 && (
                    <div className="mt-2 flex flex-wrap justify-center gap-1">
                      {(story.characterData.keyTraits as string[]).map((trait: string, idx: number) => (
                        <span key={idx} className="text-[10px] bg-slate-100 text-slate-600 rounded-full px-2 py-0.5">
                          {trait}
                        </span>
                      ))}
                    </div>
                  )}
                  
                  {story?.characterData?.colorTheme && (
                    <div className="mt-2 text-[10px] text-slate-400">
                      色调：{story.characterData.colorTheme}
                    </div>
                  )}
                </div>

                {/* 生成按钮 */}
                {!hasGenerated && !isGeneratingAll && (
                  <Button
                    onClick={generateAll}
                    disabled={isSubmitting}
                    isLoading={isSubmitting}
                    className="w-full flex items-center justify-center gap-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 mt-4"
                  >
                    <ImageIcon className="w-4 h-4" />
                    生成全部插图
                  </Button>
                )}

                {isGeneratingAll && (
                  <div className="bg-purple-50 rounded-xl mt-4 p-4">
                    <div className="flex items-center text-purple-700 gap-2 mb-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span className="text-sm font-medium">正在生成...</span>
                    </div>
                    <div className="w-full bg-purple-200 rounded-full h-2">
                      <div 
                        className="bg-purple-500 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${((progress?.completedPages || 0) / totalCount) * 100}%` }}
                      />
                    </div>
                    <p className="text-xs text-purple-600 mt-2 text-center">
                      第 {progress?.currentPage || 1} 页 / 共 {totalCount} 页
                    </p>
                  </div>
                )}
              </div>
            </Card>
          </motion.div>

          {/* 右侧：12宫格缩略图 */}
          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="lg:col-span-3">
            <Card className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4 sm:p-6">
              <div className="flex flex-col gap-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <ImageIcon className="w-5 h-5 text-pink-500" />
                    <h2 className="text-lg font-semibold">插图预览</h2>
                    {hasGenerated && (
                      <span className="text-sm text-gray-500">
                        {completedCount} / {totalCount} 页
                      </span>
                    )}
                  </div>
                  
                  {hasGenerated && !isGeneratingAll && (
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={isSubmitting}
                      isLoading={isSubmitting}
                      onClick={() => {
                        openRegenerateAllConfirm();
                      }}
                      className="flex items-center gap-2"
                    >
                      <RefreshCw className="w-4 h-4" />
                      全部重生成
                    </Button>
                  )}
                </div>

                {/* 12宫格 */}
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-6">
                  {pages.map((page, index) => {
                    const { status, imageUrl } = getImageStatus(index);
                    const isRegenerating = regeneratingPage === page.pageNumber;
                    
                    return (
                      <div
                        key={page.pageNumber}
                        className="relative aspect-[16/9] rounded-xl overflow-hidden border-2 transition-all group"
                        style={{
                          borderColor: status === 'done' ? '#ec4899' : status === 'failed' ? '#ef4444' : '#e5e7eb',
                        }}
                      >
                        {/* 图片或占位 - isRegenerating 时强制显示 loading */}
                        {isRegenerating ? (
                          <div className="w-full h-full bg-purple-50 flex flex-col items-center justify-center">
                            <Loader2 className="w-8 h-8 animate-spin text-purple-500 mb-2" />
                            <span className="text-xs text-purple-600">重新生成中...</span>
                          </div>
                        ) : status === 'done' && imageUrl ? (
                          <>
                            <img 
                              key={`page-${page.pageNumber}-${imageUrl.split('?')[0]}`}
                              src={`${imageUrl}${imageUrl.includes('?') ? '&' : '?'}_r=${Math.floor(Date.now()/1000/60)}`}
                              alt={`Page ${page.pageNumber}`}
                              className="w-full h-full object-cover"
                            />
                            {/* 悬停遮罩 */}
                            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                              <button
                                onClick={() => setPreviewPage(index)}
                                className="p-2 bg-white rounded-full text-gray-900 hover:bg-gray-100"
                                title="查看大图"
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => openRegenerateModal(page.pageNumber)}
                                className="p-2 bg-white rounded-full text-gray-900 hover:bg-gray-100"
                                title="重新生成"
                              >
                                <RefreshCw className="w-4 h-4" />
                              </button>
                            </div>
                          </>
                        ) : status === 'generating' ? (
                          <div className="w-full h-full bg-purple-50 flex flex-col items-center justify-center">
                            <Loader2 className="w-8 h-8 animate-spin text-purple-500 mb-2" />
                            <span className="text-xs text-purple-600">生成中...</span>
                          </div>
                        ) : status === 'failed' ? (
                          <div className="w-full h-full bg-red-50 flex flex-col items-center justify-center">
                            <AlertCircle className="w-8 h-8 text-red-400 mb-2" />
                            <button
                              onClick={() => openRegenerateModal(page.pageNumber)}
                              className="text-xs text-red-600 hover:text-red-700 underline"
                            >
                              点击重试
                            </button>
                          </div>
                        ) : (
                          <div className="w-full h-full bg-gray-50 flex flex-col items-center justify-center text-gray-400">
                            <ImageIcon className="w-8 h-8 mb-2" />
                            <span className="text-xs">等待生成</span>
                          </div>
                        )}
                        
                        {/* 页码标签 */}
                        <div className="absolute top-2 left-2 bg-black/60 text-white text-xs rounded-full px-3 py-1">
                          第{page.pageNumber}页
                        </div>
                      </div>
                    );
                  })}
                </div>

              </div>
            </Card>
          </motion.div>
        </div>

        {/* 底部操作按钮：独立一行，与上方卡片保持统一间距 */}
        {!isGeneratingAll && (
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center justify-between gap-2 pt-2"
          >
            <Button
              variant="secondary"
              onClick={goToPrevStage}
              className="flex items-center gap-2 bg-white border border-slate-200 text-slate-700 rounded-xl hover:bg-slate-50 whitespace-nowrap shrink-0"
            >
              <ChevronLeft className="w-4 h-4 shrink-0" />
              上一步
            </Button>

            {hasGenerated && (
              <div className="flex items-center gap-3 shrink-0">
                {!allCompleted && (
                  <span className="text-sm text-amber-600 whitespace-nowrap shrink-0 hidden sm:inline">
                    还有 {totalCount - completedCount} 页未生成
                  </span>
                )}
                <Button
                  onClick={goToNextStage}
                  disabled={!allCompleted || isCompleting}
                  isLoading={isCompleting}
                  className="flex items-center gap-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 disabled:opacity-70 whitespace-nowrap shrink-0"
                >
                  {!isCompleting && <CheckCircle className="w-4 h-4 shrink-0" />}
                  下一步
                  {!isCompleting && <ChevronRight className="w-4 h-4 shrink-0" />}
                </Button>
              </div>
            )}
          </motion.div>
        )}
      </div>
      </div>

      {/* 大图预览 Modal */}
      <AnimatePresence>
        {previewPage !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80"
            onClick={() => setPreviewPage(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="relative max-w-4xl w-full bg-white rounded-2xl overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* 关闭按钮 */}
              <button
                onClick={() => setPreviewPage(null)}
                className="absolute top-4 right-4 z-10 p-2 bg-black/50 text-white rounded-full hover:bg-black/70"
              >
                <X className="w-5 h-5" />
              </button>

              <div className="grid grid-cols-1 md:grid-cols-2">
                {/* 图片 */}
                <div className="aspect-[16/9] md:aspect-auto bg-gray-100">
                  {story?.imageUrls?.[previewPage] ? (
                    <img 
                      key={story.imageUrls[previewPage]?.split('?')[0] || `preview-${previewPage}`}
                      src={`${story.imageUrls[previewPage]}${story.imageUrls[previewPage].includes('?') ? '&' : '?'}_r=${Math.floor(Date.now()/1000/60)}`}
                      alt={`Page ${previewPage + 1}`}
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400">
                      <ImageIcon className="w-16 h-16" />
                    </div>
                  )}
                </div>

                {/* 文案信息 */}
                <div className="flex flex-col p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <span className="bg-indigo-100 text-indigo-700 text-sm rounded-full px-3 py-1">
                      第 {previewPage + 1} 页
                    </span>
                    <span className="bg-slate-100 text-slate-600 text-sm rounded-full px-3 py-1">
                      {story?.artStyle || 'pixar'} 风格
                    </span>
                  </div>

                  <h3 className="text-xl font-bold text-gray-900 mb-4">页面文案</h3>
                  <p className="text-gray-700 leading-relaxed flex-1">
                    {pages[previewPage]?.text || '暂无文案'}
                  </p>

                  {pages[previewPage]?.illustrationPrompt && (
                    <>
                      <h3 className="text-sm font-semibold text-gray-500 mt-4 mb-4">插图描述</h3>
                      <p className="text-sm text-gray-600 italic">
                        {pages[previewPage].illustrationPrompt}
                      </p>
                    </>
                  )}

                  {/* 操作按钮 */}
                  <div className="flex border-t border-slate-100 gap-4 mt-4 pt-4">
                    <Button
                      variant="secondary"
                      onClick={() => { setPreviewPage(null); openRegenerateModal(previewPage + 1); }}
                      disabled={regeneratingPage === previewPage + 1}
                      className="flex-1 flex items-center justify-center gap-2 bg-white border border-slate-200 text-slate-700 rounded-xl hover:bg-slate-50"
                    >
                      {regeneratingPage === previewPage + 1 ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <RefreshCw className="w-4 h-4" />
                      )}
                      重新生成
                    </Button>
                    <Button
                      onClick={() => setPreviewPage(null)}
                      className="flex-1 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700"
                    >
                      关闭
                    </Button>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 参考图选择 Modal（单页重绘） */}
      <AnimatePresence>
        {regenerateModalPage !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60"
            onClick={closeRegenerateModal}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="relative max-w-2xl w-full bg-white rounded-2xl overflow-hidden shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* 头部 */}
              <div className="flex items-center justify-between p-4 border-b border-slate-100">
                <h3 className="text-lg font-semibold text-gray-900">
                  重绘第 {regenerateModalPage} 页
                </h3>
                <button
                  onClick={closeRegenerateModal}
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-4 max-h-[70vh] overflow-y-auto">
                {/* 当前页面文案 */}
                <div className="mb-4 p-3 bg-indigo-50 rounded-xl">
                  <p className="text-sm text-indigo-700">
                    <span className="font-medium">文案：</span>
                    {pages[regenerateModalPage - 1]?.text || '暂无文案'}
                  </p>
                </div>

                {/* 参考图选择 */}
                <h4 className="text-sm font-medium text-gray-700 mb-2">
                  选择参考图（最多1张）
                </h4>
                <div className="mb-3 p-3 bg-amber-50 rounded-xl border border-amber-100">
                  <p className="text-xs text-amber-800 leading-relaxed">
                    <span className="font-medium">💡 建议：</span>
                    选择过多参考图会导致 AI 输出拼图或多格画面。
                    建议<span className="font-medium">不选或只选1张</span>角色最清晰、风格最满意的页面。
                    未选择时将自动使用角色肖像 + 最近成功页面作为参考。
                  </p>
                </div>

                {/* 已选计数 */}
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-gray-500">
                    已选 <span className={`font-medium ${selectedRefUrls.length > 0 ? 'text-indigo-600' : 'text-gray-400'}`}>{selectedRefUrls.length}</span> / 1 张
                  </span>
                  {selectedRefUrls.length > 0 && (
                    <button
                      onClick={() => setSelectedRefUrls([])}
                      className="text-xs text-gray-400 hover:text-gray-600 underline"
                    >
                      清空选择
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                  {(story?.imageUrls || [])
                    .map((url, idx) => ({ url, idx, pageNum: idx + 1 }))
                    .filter(item => item.url && item.pageNum !== regenerateModalPage)
                    .map(item => (
                      <div
                        key={item.idx}
                        onClick={() => {
                          setSelectedRefUrls(prev => {
                            if (prev.includes(item.url)) {
                              return prev.filter(u => u !== item.url);
                            }
                            // 最多只允许选1张
                            if (prev.length >= 1) {
                              return [item.url];
                            }
                            return [...prev, item.url];
                          });
                        }}
                        className={`relative aspect-[16/9] rounded-lg overflow-hidden border-2 cursor-pointer transition-all ${
                          selectedRefUrls.includes(item.url)
                            ? 'border-indigo-500 ring-2 ring-indigo-200'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <img
                          src={`${item.url}${item.url.includes('?') ? '&' : '?'}_r=${Math.floor(Date.now()/1000/60)}`}
                          alt={`参考图 ${item.pageNum}`}
                          className="w-full h-full object-cover"
                        />
                        {/* 勾选标记 */}
                        {selectedRefUrls.includes(item.url) && (
                          <div className="absolute top-1 right-1 w-5 h-5 bg-indigo-500 rounded-full flex items-center justify-center">
                            <CheckCircle className="w-3 h-3 text-white" />
                          </div>
                        )}
                        {/* 页码 */}
                        <div className="absolute bottom-1 left-1 bg-black/60 text-white text-[10px] rounded px-1.5 py-0.5">
                          第{item.pageNum}页
                        </div>
                      </div>
                    ))}
                </div>
              </div>

              {/* 底部按钮 */}
              <div className="flex items-center justify-end gap-3 p-4 border-t border-slate-100 bg-gray-50">
                <Button
                  variant="secondary"
                  onClick={closeRegenerateModal}
                  className="px-4 py-2 bg-white border border-slate-200 text-slate-700 rounded-xl hover:bg-slate-50"
                >
                  取消
                </Button>
                <Button
                  onClick={() => regenerateImage(regenerateModalPage, selectedRefUrls)}
                  disabled={regeneratingPage === regenerateModalPage}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {regeneratingPage === regenerateModalPage ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4" />
                  )}
                  确认重绘
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* 全部重绘确认对话框 */}
      <AnimatePresence>
        {showRegenerateAllConfirm && regenerateAllCostInfo && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60"
            onClick={closeRegenerateAllConfirm}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="relative max-w-md w-full bg-white rounded-2xl overflow-hidden shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* 头部 */}
              <div className="flex items-center justify-between p-4 border-b border-slate-100">
                <h3 className="text-lg font-semibold text-gray-900">
                  全部重绘确认
                </h3>
                <button
                  onClick={closeRegenerateAllConfirm}
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-6">
                <p className="text-sm text-gray-600 mb-4">
                  全部重绘每页将按照 <span className="font-semibold text-pink-600">¥{regenerateAllCostInfo.pricePerPage.toFixed(2)}</span>/页 计费，已生成的图片将被覆盖。
                </p>
                
                <div className="bg-slate-50 rounded-xl p-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">重绘页数</span>
                    <span className="font-medium text-gray-900">{regenerateAllCostInfo.pageCount} 页</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">免费页数</span>
                    <span className="font-medium text-green-600">- {regenerateAllCostInfo.freePages} 页</span>
                  </div>
                  <div className="border-t border-slate-200 pt-2 flex justify-between text-sm">
                    <span className="text-gray-500">计费页数</span>
                    <span className="font-medium text-gray-900">{regenerateAllCostInfo.chargeablePages} 页 × ¥{regenerateAllCostInfo.pricePerPage.toFixed(2)}</span>
                  </div>
                  <div className="border-t border-slate-200 pt-2 flex justify-between">
                    <span className="text-base font-semibold text-gray-900">实际扣费</span>
                    <span className="text-xl font-bold text-pink-600">¥{regenerateAllCostInfo.cost.toFixed(2)}</span>
                  </div>
                </div>
                
                {regenerateAllCostInfo.cost === 0 && (
                  <div className="mt-3 p-3 bg-green-50 rounded-xl">
                    <p className="text-sm text-green-700 text-center">
                      🎉 本次全部重绘在免费额度内，不扣费
                    </p>
                  </div>
                )}
              </div>

              {/* 底部按钮 */}
              <div className="flex items-center justify-end gap-3 p-4 border-t border-slate-100 bg-gray-50">
                <Button
                  variant="secondary"
                  onClick={closeRegenerateAllConfirm}
                  className="px-4 py-2 bg-white border border-slate-200 text-slate-700 rounded-xl hover:bg-slate-50"
                >
                  取消
                </Button>
                <Button
                  onClick={() => {
                    closeRegenerateAllConfirm();
                    generateAll();
                  }}
                  disabled={isSubmitting}
                  className="px-4 py-2 bg-pink-600 text-white rounded-xl hover:bg-pink-700 disabled:opacity-50 flex items-center gap-2"
                >
                  <RefreshCw className="w-4 h-4" />
                  确定重绘
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
